import { hourlyExtrema } from '../../data/temperatures'
import type { HourView } from '../../../app/time/hourModels'
import type { Range, WeatherReport } from '../../../shared/contract'
import { fmtPrecip, fmtTemp, fmtTime, fmtWind, temp, type Units } from '../../../shared/units'

export const HOUR = 3_600_000
export const finite = (v: number | null | undefined): v is number => v != null && Number.isFinite(v)
const xy = (x: number, y: number) => `${x.toFixed(2)},${y.toFixed(2)}`

/** Missing values and missing timestamps both lift the pen. */
export function linePath(hours: readonly HourView[], x: (t: number) => number, value: (h: HourView) => number | null, y: (n: number) => number): string {
  let last = -Infinity
  return hours.map((h) => {
    const v = value(h)
    if (!finite(v)) { last = -Infinity; return '' }
    const command = h.t - last === HOUR ? 'L' : 'M'
    last = h.t
    return command + xy(x(h.t), y(v))
  }).join(' ')
}

export function validRange(range: Range | null | undefined): range is Range {
  return !!range && finite(range.low) && finite(range.high) && range.low <= range.high
}

function intervalPath(hours: readonly HourView[], x: (t: number) => number, y: (n: number) => number, unit: Units['temp']): string {
  const runs: HourView[][] = []
  for (const h of hours) {
    if (!finite(h.tempValue) || !validRange(h.hour.temperature_range)) continue
    const last = runs.at(-1)
    if (last && h.t - last.at(-1)!.t === HOUR) last.push(h)
    else runs.push([h])
  }
  return runs.filter((run) => run.length > 1).map((run) => {
    const side = (items: HourView[], bound: 'low' | 'high') => items.map((h) => xy(x(h.t), y(temp(h.hour.temperature_range![bound], unit)!)))
    return `M${side(run, 'high').join(' L')} L${side([...run].reverse(), 'low').join(' L')} Z`
  }).join(' ')
}

const prepared = new WeakMap<readonly HourView[], { report: WeatherReport; units: Units; chart: ReturnType<typeof buildPanorama> }>()
export function getPanorama(hours: readonly HourView[], report: WeatherReport | undefined, units: Units) {
  const hit = prepared.get(hours)
  if (hit && hit.report === report && hit.units === units) return hit.chart
  const chart = buildPanorama(hours, report, units)
  if (report) prepared.set(hours, { report, units, chart })
  return chart
}

export function buildPanorama(hours: readonly HourView[], report: WeatherReport | undefined, units: Units) {
  const start = hours[0]?.t ?? 0
  // 48 hourly slots, on the instant axis: gaps never compress time or extend the horizon.
  const end = start + 47 * HOUR
  const series = hours.filter((h) => h.t >= start && h.t <= end)
  const x = (t: number) => (t - start) / (end - start) * 1000
  const values = series.flatMap((h) => [h.hour.temperature_c, ...(validRange(h.hour.temperature_range) ? [h.hour.temperature_range.low, h.hour.temperature_range.high] : [])]).filter(finite)
  const lowC = values.length ? Math.floor(Math.min(...values)) - 2 : 0
  const highC = values.length ? Math.ceil(Math.max(...values)) + 2 : 10
  const low = temp(lowC, units.temp)!, high = temp(highC, units.temp)!
  const y = (v: number) => 85 - (v - low) / (high - low) * 70
  const rainMax = Math.max(1, ...series.map((h) => h.hour.precip_mm).filter(finite))
  const windMax = Math.max(5, ...series.map((h) => h.hour.wind_ms).filter(finite))
  const temperatures = series.filter((h) => finite(h.tempValue))
  const extremes = hourlyExtrema(series.map(h => h.hour))
  const peak = series.find(h => h.hour === extremes.high) ?? null
  const coolest = series.find(h => h.hour === extremes.low) ?? null
  const nights: { left: number; right: number }[] = []
  for (const h of series) {
    if (h.isDay) continue
    const left = x(h.t), right = x(Math.min(end, h.t + HOUR))
    const last = nights.at(-1)
    if (last?.right === left) last.right = right
    else nights.push({ left, right })
  }
  const eventMap = new Map<string, { time: number; label: string; kind: 'sunrise' | 'sunset' | 'moonrise' | 'moonset'; stamp: string; x: number }>()
  const event = (kind: 'sunrise' | 'sunset' | 'moonrise' | 'moonset', iso: string | null | undefined) => {
    const time = iso ? Date.parse(iso) : NaN
    if (!finite(time) || time < start || time > end) return
    const stamp = fmtTime(iso, report!.location.timezone, units.clock)
    eventMap.set(`${kind}-${time}`, { time, label: `${kind[0].toUpperCase()}${kind.slice(1)} ${stamp}`, kind, stamp, x: x(time) / 10 })
  }
  report?.daily.forEach((d) => { event('sunrise', d.sunrise); event('sunset', d.sunset); event('moonrise', d.moonrise); event('moonset', d.moonset) })
  event('sunrise', report?.astronomy.sunrise); event('sunset', report?.astronomy.sunset)
  event('moonrise', report?.astronomy.moon?.moonrise); event('moonset', report?.astronomy.moon?.moonset)
  const occurrences = new Map<string, number>()
  const events = [...eventMap.values()].sort((a, b) => a.time - b.time).map(event => {
    const slot = occurrences.get(event.kind) ?? 0
    occurrences.set(event.kind, slot + 1)
    return { ...event, key: `${event.kind}-${slot}` }
  })
  const samples = series.map((h) => ({
    hour: h, x: x(h.t) / 10, y: finite(h.tempValue) ? y(h.tempValue) / 100 : null,
    readout: `Rain ${h.precip ?? '—'} · chance ${h.chance ?? '—'} · wind ${h.wind ?? '—'}`,
  }))
  return {
    start, end, series, x, y, samples, nights, events, peak, coolest,
    low: fmtTemp(lowC, units.temp), high: fmtTemp(highC, units.temp),
    temperature: linePath(series, x, (h) => finite(h.tempValue) ? h.tempValue : null, y),
    band: intervalPath(series, x, y, units.temp),
    chance: linePath(series, x, (h) => finite(h.chanceValue) ? h.chanceValue : null, (v) => 94 - v / 100 * 80),
    wind: linePath(series, x, (h) => finite(h.hour.wind_ms) ? h.hour.wind_ms : null, (v) => 94 - v / windMax * 80),
    rainMax, rainLabel: fmtPrecip(rainMax, units.precip), windLabel: fmtWind(windMax, units.wind),
    hasTemperature: temperatures.length > 0,
    hasRain: series.some((h) => finite(h.hour.precip_mm)),
    hasChance: series.some((h) => finite(h.chanceValue)),
    hasWind: series.some((h) => finite(h.hour.wind_ms)),
    dry: series.length >= 6 && series.every((h, i) => h.t === start + i * HOUR && finite(h.hour.precip_mm) && h.hour.precip_mm < .1 && !(h.hour.snow_cm! > 0) && finite(h.chanceValue) && h.chanceValue <= 10),
  }
}
